import React from 'react';
import { useTranslation } from 'react-i18next';
import { flagFor } from '../../../../scouting/recording';

export interface TalliesPest {
  id: string;
  code: string;
  name: string;
  measurementType: string;
  actionThreshold: number;
}

export interface OrchardTalliesBarProps {
  levelPests: TalliesPest[];
  countPests: TalliesPest[];
  /** Current level values keyed by pest id (0–4) */
  levels: Record<string, number>;
  /** Current count values keyed by pest id */
  counts: Record<string, number>;
  onRaise: (id: string) => void;
  onLower: (id: string) => void;
  onInc: (id: string) => void;
  onDec: (id: string) => void;
}

const LEVEL_GLYPHS = ['–', 'x', 'xx', 'xxx', 'xxxx'];

export function OrchardTalliesBar({
  levelPests,
  countPests,
  levels,
  counts,
  onRaise,
  onLower,
  onInc,
  onDec,
}: OrchardTalliesBarProps) {
  const { t } = useTranslation();

  if (levelPests.length === 0 && countPests.length === 0) return null;

  return (
    <div className="owbar" style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '12px 16px', marginBottom: '14px', borderLeft: '3px solid var(--green-500)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '8px', maxWidth: '220px' }}>
        <span className="lbl" style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--fg-muted)' }}>
          {t('fieldScout.desktop.tallies.label')}
        </span>
        <span style={{ fontSize: '11px', color: 'var(--fg-muted)' }}>
          {t('fieldScout.desktop.tallies.note')}
        </span>
      </div>

      <div className="ow-rows" style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
        {levelPests.length > 0 && (
          <div className="ow-row ow-row--level" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
      {levelPests.map(pest => {
        const value = levels[pest.id] ?? 0;
        const exceeded = flagFor(value, pest.actionThreshold) === 'exceeded';
        return (
          <div key={pest.id} className="ow lev" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="code" style={{ fontWeight: 900, fontSize: '14px', color: 'var(--purple-700)' }}>
              {pest.code}
            </span>
            {exceeded && (
              <span
                data-testid={`ow-flag-${pest.id}`}
                style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'var(--red-500)', color: '#fff', fontSize: '10px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                !
              </span>
            )}
            <div className="stepper" style={{ display: 'inline-flex', border: '1px solid var(--neutral-300)', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                data-testid={`ow-lower-${pest.id}`}
                onClick={() => onLower(pest.id)}
                style={{ width: '30px', height: '32px', border: 0, background: 'var(--neutral-100)', fontSize: '16px', fontWeight: 800, color: 'var(--neutral-700)', cursor: 'pointer' }}
                aria-label={`Lower ${pest.code}`}
              >
                −
              </button>
              <span style={{ minWidth: '46px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontWeight: 800, fontSize: '14px' }}>
                {LEVEL_GLYPHS[value] ?? LEVEL_GLYPHS[0]}
              </span>
              <button
                data-testid={`ow-raise-${pest.id}`}
                onClick={() => onRaise(pest.id)}
                style={{ width: '30px', height: '32px', border: 0, background: 'var(--neutral-100)', fontSize: '16px', fontWeight: 800, color: 'var(--neutral-700)', cursor: 'pointer' }}
                aria-label={`Raise ${pest.code}`}
              >
                +
              </button>
            </div>
          </div>
        );
      })}
          </div>
        )}
        {countPests.length > 0 && (
          <div className="ow-row ow-row--count" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
      {countPests.map(pest => {
        const value = counts[pest.id] ?? 0;
        const exceeded = flagFor(value, pest.actionThreshold) === 'exceeded';
        return (
          <div key={pest.id} className="ow cnt" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="code" style={{ fontWeight: 900, fontSize: '14px', color: 'var(--green-700)' }}>
              {pest.code}
            </span>
            {exceeded && (
              <span
                data-testid={`ow-flag-${pest.id}`}
                style={{ width: '16px', height: '16px', borderRadius: '50%', background: 'var(--red-500)', color: '#fff', fontSize: '10px', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                !
              </span>
            )}
            <div className="stepper" style={{ display: 'inline-flex', border: '1px solid var(--neutral-300)', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                data-testid={`ow-dec-${pest.id}`}
                onClick={() => onDec(pest.id)}
                style={{ width: '30px', height: '32px', border: 0, background: 'var(--neutral-100)', fontSize: '16px', fontWeight: 800, color: 'var(--neutral-700)', cursor: 'pointer' }}
                aria-label={`Decrease ${pest.code}`}
              >
                −
              </button>
              <span style={{ minWidth: '46px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontWeight: 800, fontSize: '14px' }}>
                {value}
              </span>
              <button
                data-testid={`ow-inc-${pest.id}`}
                onClick={() => onInc(pest.id)}
                style={{ width: '30px', height: '32px', border: 0, background: 'var(--neutral-100)', fontSize: '16px', fontWeight: 800, color: 'var(--neutral-700)', cursor: 'pointer' }}
                aria-label={`Increase ${pest.code}`}
              >
                +
              </button>
            </div>
          </div>
        );
      })}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrchardTalliesBar;
